import "./VideoMention.css";
import { onMount, Show } from "solid-js";
import type { VideoEvidence, VideoEvidenceInfluencer } from "../ChatEventTypes";
import {
	installMentionClipboard,
	videoMentionUrl,
} from "../utils/mentionClipboard";
import {
	isMentionClip,
	type PlayVideoMentionsHandler,
	playVideoMentions,
} from "../utils/videoMentionPlayback.ts";
import { VideoMentionMarker } from "./VideoMentionMarker.tsx";

export type VideoMentionProps = {
	id: string;
	index: number;
	start_time_sec: number | null;
	end_time_sec: number | null;
};

export type MentionedVideoItem = VideoMentionProps & {
	thumbnail_url?: string | null;
	aspect_ratio?: number | null;
	title?: string | null;
	video_id?: string | null;
	video_url?: string | null;
	/** Full source video duration in seconds. */
	video_duration?: number | null;
	hls_ready?: boolean;
	influencer?: VideoEvidenceInfluencer | null;
	social_account?: VideoEvidence["social_account"] | null;
	view_count?: number | null;
	post_url?: string | null;
};

type VideoMentionComponentProps = {
	mention: MentionedVideoItem;
	allVideoMentions?: MentionedVideoItem[];
	appEmbedded?: boolean;
	interactionId?: string;
	onPlayVideoMentions?: PlayVideoMentionsHandler;
};

export const VideoMention = (props: VideoMentionComponentProps) => {
	// Copying a citation writes the link below in the QuestionBox's own form, so
	// that the clip survives being pasted into another app (see mentionClipboard).
	onMount(installMentionClipboard);

	const playMentionedVideo = () => {
		const mentions = props.allVideoMentions ?? [props.mention];
		playVideoMentions(mentions, props.mention.id, {
			appEmbedded: props.appEmbedded,
			interactionId: props.interactionId,
			onPlayVideoMentions: props.onPlayVideoMentions,
		});
	};

	return (
		<span
			class="video-mention"
			data-video-mention-id={props.mention.id}
			onPointerDown={(event) => event.preventDefault()}
			onMouseDown={(event) => event.preventDefault()}
			onClick={playMentionedVideo}
		>
			<Show
				when={videoMentionUrl(props.mention)}
				fallback={
					<VideoMentionMarker
						index={props.mention.index}
						clip={isMentionClip(props.mention)}
					/>
				}
			>
				{(href) => (
					// Not a tab stop of its own, and never followed: the marker around it
					// is what you click, and clicking still opens the clip.
					<a
						class="video-mention-link"
						href={href()}
						tabIndex={-1}
						draggable={false}
						onClick={(event) => event.preventDefault()}
					>
						<VideoMentionMarker
							index={props.mention.index}
							clip={isMentionClip(props.mention)}
						/>
					</a>
				)}
			</Show>
		</span>
	);
};
